export const SUPPORTED_SOCIAL_PLATFORMS = [
  "instagram",
  "x",
  "tiktok",
  "threads",
  "reddit",
] as const;

export type SocialPlatform = (typeof SUPPORTED_SOCIAL_PLATFORMS)[number];
export type CrawlPlatform = "website" | "bluesky" | SocialPlatform;
export type CrawlJobType = "web" | "social";
export type CrawlJobStatus = "queued" | "crawling" | "done" | "partial_success" | "failed";
export type CrawlDiscoveryMethod = "sitemap" | "link_traversal" | "provider";
export type CrawlDiscoveryExtent = "complete_for_scope" | "partial" | "unknown";
export type CrawlDiscoveryState = "discovered" | "excluded" | "not_observed" | "removed_with_evidence";
export type CrawlCaptureState = "captured" | "not_attempted" | "failed";
export type PropertyContentState = "text" | "non_text";

export interface CrawlInventoryItem {
  source_key: string;
  source_url: string;
  source_type?: "web_page" | "social_post" | "social_bio";
  /** Required for social_bio/social_post; optional for backward-compatible website items. */
  content_state?: PropertyContentState;
  /** Eligible discovered identities redirected to this captured source URL. */
  redirected_from_urls?: string[];
  title?: string;
  discovery_state: CrawlDiscoveryState;
  capture_state: CrawlCaptureState;
  failure_reason?: string | null;
  exclusion_reason?: string | null;
  raw_datum_id?: string | null;
  content_hash?: string | null;
  last_checked_at?: string | null;
  last_successful_capture_at?: string | null;
  /** Bounded provider facts used by social catalog/detail projections. */
  provider_metadata?: Record<string, unknown>;
}

export interface CrawlInventoryReceipt {
  surface_id: string;
  discovery_method: CrawlDiscoveryMethod;
  discovery_extent: CrawlDiscoveryExtent;
  scope_label: string;
  capture_budget: number;
  discovered_count: number;
  eligible_count: number;
  attempted_count: number;
  captured_count: number;
  failed_count: number;
  excluded_count: number;
  unattempted_count: number;
  items: CrawlInventoryItem[];
  checked_at?: string;
  failure_reason?: string | null;
  /** Narrow provider scope/coverage receipt; full authored bodies never belong here. */
  provider_scope?: Record<string, unknown>;
}

type PropertyContentCatalogItemBase = Omit<CrawlInventoryItem, "source_type" | "content_state"> & {
  id: string;
  surface_id: string;
  revision_count: number;
  current_document_id: string | null;
  current_document_content?: string | null;
};

export type PropertyContentCatalogItem = PropertyContentCatalogItemBase & (
  | { source_type: "web_page"; content_state?: PropertyContentState }
  | { source_type: "social_post" | "social_bio"; content_state: PropertyContentState }
);

export interface PropertyContentCatalog {
  surface_id: string;
  latest_job_id: string | null;
  latest_status: CrawlJobStatus | null;
  inventory: CrawlInventoryReceipt | null;
  items: PropertyContentCatalogItem[];
}

export interface KnowledgeBaseEntry {
  id: string;
  brand_id: string;
  crawl_job_id: string;
  surface_id?: string;
  catalog_item_id?: string;
  raw_datum_id?: string;
  content_hash?: string;
  source_key: string;
  source_url: string;
  source_type: "web_page" | "social_post" | "social_bio" | "product_description";
  platform: CrawlPlatform | "shopify";
  title?: string;
  content: string;
  chunk_index: number;
  chunk_total: number;
  /** New crawl writes remain hidden until their canonical Run reaches success. */
  search_visibility?: "pending" | "ready";
  metadata: {
    surface_id?: string;
    raw_datum_id?: string;
    content_hash?: string;
    post_id?: string;
    permalink?: string;
    timestamp?: string;
    engagement?: { likes?: number; comments?: number };
    [key: string]: unknown;
  };
  indexed_at: string;
}

export interface CrawlJobError {
  target: string;
  message: string;
  at: string;
  attempt?: number;
}

export interface CrawlJob {
  id: string;
  brand_id: string;
  type: CrawlJobType | "shopify_import";
  url: string;
  platform?: CrawlPlatform | "shopify";
  surface_id?: string;
  crawl_scope?: "site" | "page";
  status: CrawlJobStatus;
  item_count: number;
  pages_attempted?: number;
  pages_failed?: number;
  inventory?: CrawlInventoryReceipt;
  errors?: CrawlJobError[];
  error?: string;
  created_at: string;
  updated_at?: string;
  completed_at?: string;
  refreshed_by?: string;
  caption_budget_root_id?: string;
  shopify_connection_id?: string;
  shop?: string;
  shopify_bulk_operation_id?: string;
  jsonl_url?: string;
  product_count_estimated?: number;
  product_count_actual?: number;
  image_count_estimated?: number;
  image_count_actual?: number;
  estimated_cost_cents?: number;
  cost_ceiling_cents?: number;
  actual_cost_cents?: number;
  captions_charged_cents?: number;
  shopify_bulk_op_status?: string;
  collection_id?: string;
}
